import React from "react"
import Typography from "../../../../ui/atoms/Typography"
import RecapGrid, { RecapGridItem } from "../../../../ui/organisms/RecapGrid"
import { toItaDate } from "../../../../utils/dates"
import { useLabels } from "../../../contents/hooks"
import { getStatusLabel, OrderStatus } from "../../converters/labels"
import { CompletedOrder } from "../../types"
import GiftcardRecap from "../../../../ui/organisms/GiftcardRecap"
import { GIFTCARD_IMAGE_REF } from "../../../checkout/data"

interface Props {
  order: CompletedOrder
}

const OrderInfoData = ({ order }: Props) => {
  const { getLabel } = useLabels()
  const hasGiftcards = order.items.some((item) => item.assignedGiftCardCode)
  const lines: RecapGridItem[] = [
    {
      label: getLabel("common", "contact"),
      content: order.customer.email,
    },
    ...(!hasGiftcards && order.shipping.shippingMethod === "courier"
      ? [
          {
            label: getLabel("order", "shipping-address"),
            content: `${order.shipping.shippingAddress.address}, ${order.shipping.shippingAddress.city}, ${order.shipping.shippingAddress.zip}`,
          },
        ]
      : []),
    ...(!hasGiftcards && order.shipping.shippingMethod === "store"
      ? [
          {
            label: getLabel("order", "shipping-collect"),
            content: (
              <>
                <p className="my-2">
                  {[
                    order.shipping.store?.company,
                    order.shipping.store?.name,
                  ].join(" ")}
                </p>
                <p className="pb-2 my-2">
                  {order.shipping.store?.address.trim()},{" "}
                  {order.shipping.store?.cap} - {order.shipping.store?.city}{" "}
                  {order.shipping.store?.city !== order.shipping.store?.province
                    ? `(${order.shipping.store?.province})`
                    : ""}
                </p>
              </>
            ),
          },
        ]
      : []),
    {
      label: getLabel("order", "order-date"),
      content: <>{toItaDate(new Date(order.createdAt))}</>,
    },
    ...(!hasGiftcards
      ? [
          {
            label: getLabel("order", "order-state"),
            content: (
              <>
                <span className="text-successText">
                  {getStatusLabel(order.status as OrderStatus)}
                </span>{" "}
                {(order.status as OrderStatus) === "sent" &&
                  order.tracking?.url && (
                    <>
                      <span className="px-2">-</span>
                      <a
                        href={order.tracking.url}
                        className="underline"
                        target="blank"
                      >
                        {getLabel("order", "track-order")}
                      </a>
                    </>
                  )}
                {(order.status as OrderStatus) === "delivered" &&
                  order.delivery?.deliveredDate && (
                    <>
                      <Typography>
                        {toItaDate(new Date(order.delivery.deliveredDate))}
                      </Typography>
                    </>
                  )}
              </>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <Typography variant="subtitle2" weight="normal">
        {getLabel("order", "order")} {order.id}
      </Typography>
      <div className="my-8">
        <RecapGrid items={lines} />
      </div>
      <div className="flex flex-col items-center justify-center my-4 gap-2">
        {hasGiftcards &&
          order.items
            .filter((x) => x.assignedGiftCardCode)
            .map((x) => (
              <GiftcardRecap
                key={x.assignedGiftCardCode}
                amount={x.order.total.originalPrice}
                code={x.assignedGiftCardCode as string}
                imageAsset={GIFTCARD_IMAGE_REF}
              />
            ))}
      </div>
    </>
  )
}

export default OrderInfoData
